/**
 * 受領書ページ(v3.8 1-6 / v1.1 §4 / 2026-08-26 押印式電子署名)。
 * 詳細ポップアップ等の「受領書を出力」から同じタブで開く。
 *
 * モード(URLの ?mode= / &sign=):
 * - 既定(支払先用): 支払先が署名する紙の受領書。印刷 or PDFで保存
 * - internal(本人記入用): 経理→本人の受け渡し用。印刷 or PDFで保存
 * - internal&sign=receive|settle(電子署名): 内容を確認のうえ、署名欄にログイン者の
 *   フルネームが印字され(画面ではマーカー表示)、「押印」をクリックすると苗字の朱印が
 *   押される。そのまま署名済み受領書PDFを添付して受領を記録できる(印刷・スキャン不要)
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchUsers } from '@core/masters/masterRepo';
import { uploadFiles } from '@core/expense/attachments';
import { confirmReceive, pay, settleReceive } from './flowActions';
import { reportFlowResult } from './flowResult';
import { buildReceiptPdfFile, downloadFile, loadImage } from './signedReceipt';
import SignaturePadModal from './SignaturePadModal';
import { formatYen } from '@core/lib/money';
import { toDateStr } from '@core/lib/date';
import { PAYMENT_METHOD_LABELS, type Company, type Transaction, type User } from '@core/lib/types';

export default function ReceiptPrintPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  /** internal=本人記入用。sign=receive|settle は押印式の電子署名モード */
  const internal = searchParams.get('mode') === 'internal';
  const signParam = searchParams.get('sign');
  const signMode: 'receive' | 'settle' | null =
    internal && (signParam === 'receive' || signParam === 'settle') ? signParam : null;
  /** sign=pay(仮払い): 支払先にその場でサインをもらい、署名入りPDFを添付して支払い済みにする(2026-08-26) */
  const signPay = !internal && signParam === 'pay';
  const payDate = searchParams.get('date') || toDateStr(new Date());

  const [tx, setTx] = useState<Transaction | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState('');
  const [stamped, setStamped] = useState(false);
  const [busy, setBusy] = useState(false);
  /** 支払先の手書きサイン(2026-08-26): サインパッドで書いた署名画像(データURL) */
  const [handSignature, setHandSignature] = useState<string | null>(null);
  const [padOpen, setPadOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const snap = await getDoc(doc(getDb(), 'transactions', id));
        if (!snap.exists()) {
          setError('申請が見つかりません');
          return;
        }
        const t = { id: snap.id, ...(snap.data() as object) } as Transaction;
        setTx(t);
        const [companies, userList] = await Promise.all([fetchCompanies(), fetchUsers()]);
        setCompany(companies.find((c) => c.id === (t.company_id ?? 'sum')) ?? null);
        setUsers(userList);
      } catch (e) {
        setError(e instanceof Error ? e.message : '読み込みに失敗しました');
      }
    })();
  }, [id]);

  const renderParams = useMemo(() => {
    if (!tx) return null;
    const applicantName = users.find((u) => u.id === tx.created_by)?.display_name ?? '—';
    const payerName =
      signMode && internal
        ? (user?.display_name ?? applicantName)
        : (users.find((u) => u.id === (tx.paid_by ?? tx.created_by))?.display_name ?? applicantName);
    const kindNote = internal ? (signMode === 'settle' || tx.advance ? '立替精算金' : '仮払い金') : null;
    return {
      applicantName,
      // 内部(本人記入用): 受領者(本人)=本人名 / 支払先=申請の支払先。
      // 支払先用(2026-08-27): 支払い者=支払った本人名のみ。受領者行は出さない(署名する人=受領者)
      receiverLabel: internal ? '受領者(本人)' : '支払い者',
      receiverName: payerName,
      counterpartyLabel: internal ? '支払先' : null,
      counterpartyName: internal ? (tx.counterparty ?? '') : '',
      kindNote,
      /** 苗字 = 表示名の先頭(空白区切りの1語目) */
      surname: (user?.display_name ?? '').split(/[ 　]/)[0] || (user?.display_name ?? ''),
    };
  }, [tx, users, internal, signMode, user]);

  if (error) return <p style={{ padding: 40 }}>{error}</p>;
  if (!tx || !renderParams) return <p style={{ padding: 40 }}>読み込み中…</p>;

  const today = toDateStr(new Date());
  const [y, m, d] = today.split('-');
  const methodLabel = tx.payment_method ? PAYMENT_METHOD_LABELS[tx.payment_method] : '現金';

  /** 現在の表示内容と同じ受領書PDFを作る(押印・手書きサインがあればそのまま入る) */
  async function buildPdf() {
    const signed = signMode !== null && stamped;
    const signatureImage = handSignature ? await loadImage(handSignature) : null;
    return buildReceiptPdfFile(
      {
        transaction: tx!,
        companyName: company?.name ?? 'SUM',
        receiverLabel: renderParams!.receiverLabel,
        receiverName: renderParams!.receiverName,
        kindNote: renderParams!.kindNote,
        // 受領日は当日(2026-08-27: 二重表示のため編集欄は廃止)
        receiptDate: new Date(),
        counterpartyLabel: renderParams!.counterpartyLabel,
        counterpartyName: renderParams!.counterpartyName || null,
        signatureName: signed ? (user?.display_name ?? null) : null,
        stampName: signed ? renderParams!.surname : null,
        signatureImage,
      },
      signed || signatureImage ? `受領書_署名済み_${tx!.code ?? ''}.pdf` : `受領書_${tx!.code ?? ''}.pdf`,
    );
  }

  async function savePdf() {
    setBusy(true);
    try {
      downloadFile(await buildPdf());
      toast('受領書PDFを保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'PDFの作成に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /** 電子署名: 押印済みの受領書PDFを添付して、受領(仮払い)/精算受領(立替)を記録する */
  async function attachAndRecord() {
    if (!user || !tx || !signMode) return;
    if (!stamped) {
      toast('先に「押印」をクリックしてください');
      return;
    }
    setBusy(true);
    try {
      const file = await buildPdf();
      const kind = signMode === 'receive' ? 'received_receipts' : 'settlement_receipts';
      const receipts = await uploadFiles(tx.id, kind, [file], user.id);
      if (signMode === 'receive') {
        const result = await confirmReceive({ transaction: tx, userId: user.id, receipts });
        reportFlowResult(toast, `${tx.code} の受領を記録しました(署名済み受領書を添付)。支払いページから支払いを実行してください`, result);
      } else {
        const result = await settleReceive({ transaction: tx, userId: user.id, receipts });
        reportFlowResult(toast, `${tx.code} の精算受領書を記録しました。承認者の完了確認に進みます`, result);
      }
      navigate('/receive');
    } catch (e) {
      toast(e instanceof Error ? e.message : '記録に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /** 仮払い: 支払先の手書きサイン入り受領書PDFを証憑として添付し、支払い済みにする(実績計上) */
  async function attachAndPay() {
    if (!user || !tx) return;
    if (!handSignature) {
      toast('先に「手書きサインをもらう」で支払先のサインを受けてください');
      return;
    }
    setBusy(true);
    try {
      const file = await buildPdf();
      const receipts = await uploadFiles(tx.id, 'receipts', [file], user.id);
      const result = await pay({
        transaction: tx,
        input: {
          paymentDate: payDate,
          paymentMethod: tx.payment_method ?? 'prepaid',
          paymentAccountId: null,
          receipts,
          cashAccountId: null,
          amount: tx.amount,
          accountId: tx.account_id || undefined,
          subAccountId: tx.sub_account_id || undefined,
          postingDate: tx.posting_date || undefined,
        },
        userId: user.id,
      });
      reportFlowResult(toast, `${tx.code} を支払い済みにしました(署名入り受領書を添付)。実績に計上されます`, result);
      navigate('/pay');
    } catch (e) {
      toast(e instanceof Error ? e.message : '支払いの記録に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ background: '#fff', minHeight: '100vh', color: '#1a1a1a', fontFamily: '"Hiragino Mincho ProN", "Yu Mincho", serif' }}>
      <style>{`
        @media print { .no-print { display: none !important; } body { background: #fff; } .receipt-sheet { zoom: 1; margin: 40px auto; } }
        @page { size: A4 portrait; margin: 18mm; }
        /* 画面では縮小表示して、上下スクロールなしで1ページに収める(2026-08-26)。印刷・PDFは等倍 */
        @media screen { .receipt-sheet { zoom: 0.72; } }
      `}</style>

      <div className="no-print" style={{ padding: '14px 20px', background: '#F4F7FB', borderBottom: '1px solid #ddd', fontFamily: 'sans-serif', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>
          受領書
          {signMode ? '(電子署名)' : internal ? '(本人記入用)' : '(支払先用)'}
        </b>
        <span style={{ fontSize: 12.5, color: '#666', maxWidth: 560 }}>
          {signMode
            ? '内容を確認し、右下の「押印」をクリックしてください。苗字の印が押されたら「添付して記録」で完了します(印刷・スキャン不要)。'
            : signPay
              ? 'タブレット・スマホを支払先に渡して「手書きサインをもらう」でサインを受けると、「添付して支払い済みにする」で署名入りPDFが自動添付されます(印刷・スキャン不要)。紙で受け取る場合は印刷し、支払いモーダルからスキャンを添付してください。'
              : '「PDFで保存」でそのままPDFになります(印刷も可能)。'}
        </span>
        <button
          onClick={() => window.history.back()}
          style={{ marginLeft: 'auto', padding: '8px 18px', background: '#fff', color: '#024BD5', border: '1px solid #024BD5', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
        >
          ← 戻る
        </button>
        <button
          onClick={() => void savePdf()}
          disabled={busy}
          style={{ padding: '8px 22px', background: '#024BD5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
        >
          PDFで保存
        </button>
        <button
          onClick={() => window.print()}
          style={{ padding: '8px 18px', background: '#fff', color: '#024BD5', border: '1px solid #024BD5', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}
        >
          印刷する
        </button>
        {/* 支払先用: タブレット・スマホでその場でサインをもらえる(2026-08-26) */}
        {!internal && (
          <button
            onClick={() => setPadOpen(true)}
            style={{ padding: '8px 18px', background: '#1B8A5A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer', fontWeight: 700 }}
          >
            {handSignature ? 'サインを書き直す' : '手書きサインをもらう'}
          </button>
        )}
        {signMode && (
          <button
            onClick={() => void attachAndRecord()}
            disabled={busy || !stamped}
            title={stamped ? undefined : '先に「押印」をクリックしてください'}
            style={{ padding: '8px 22px', background: stamped ? '#1B8A5A' : '#9BB0A5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: stamped ? 'pointer' : 'not-allowed', fontWeight: 700 }}
          >
            {busy ? '処理中…' : signMode === 'receive' ? '添付して受領を記録' : '添付して精算受領を記録'}
          </button>
        )}
        {/* 仮払いの支払い: サイン受領後、署名入りPDFを添付して支払い済みにする(2026-08-26) */}
        {signPay && (
          <button
            onClick={() => void attachAndPay()}
            disabled={busy || !handSignature}
            title={handSignature ? undefined : '先に「手書きサインをもらう」でサインを受けてください'}
            style={{ padding: '8px 22px', background: handSignature ? '#1B8A5A' : '#9BB0A5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, cursor: handSignature ? 'pointer' : 'not-allowed', fontWeight: 700 }}
          >
            {busy ? '処理中…' : '添付して支払い済みにする(実績計上)'}
          </button>
        )}
      </div>

      <div className="receipt-sheet" style={{ maxWidth: 680, margin: '16px auto', padding: '0 24px' }}>
        <div style={{ textAlign: 'right', fontSize: 12, color: '#555' }}>
          管理番号: {tx.code}
          <br />
          発行日: {y}年{Number(m)}月{Number(d)}日
        </div>

        <h1 style={{ textAlign: 'center', fontSize: 30, letterSpacing: '1.2em', textIndent: '1.2em', margin: '28px 0 34px' }}>受領書</h1>

        <div style={{ fontSize: 16, borderBottom: '1px solid #1a1a1a', paddingBottom: 4, marginBottom: 30, width: '60%' }}>
          {company?.name ?? 'SUM'} 御中
        </div>

        <div style={{ border: '2px solid #1a1a1a', padding: '14px 20px', textAlign: 'center', margin: '0 40px 26px' }}>
          <span style={{ fontSize: 14, marginRight: 18 }}>金 額</span>
          <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '.06em' }}>¥{formatYen(tx.amount)} −</span>
          <span style={{ fontSize: 12, marginLeft: 10 }}>(税込)</span>
        </div>

        <div style={{ fontSize: 14, lineHeight: 2 }}>
          <div>
            但し: {tx.description ?? ''}
            {renderParams.kindNote ? `(${renderParams.kindNote})` : ''}
          </div>
          <div style={{ marginTop: 6 }}>上記の金額を、正に受領いたしました。</div>
        </div>

        <div style={{ marginTop: 44, fontSize: 14, lineHeight: 2.4 }}>
          {/* 受領日は当日のみ表示(2026-08-27: 二重表示だった編集欄は廃止) */}
          <div>
            受領日: {y}年{Number(m)}月{Number(d)}日
          </div>
          <div>
            {renderParams.receiverLabel}: {renderParams.receiverName || (
              <span style={{ display: 'inline-block', width: 260, borderBottom: '1px solid #1a1a1a', minHeight: 22 }} />
            )}
          </div>
          {/* 内部のみ支払先行を表示。支払先用は行なし(署名する人=受領者のため・2026-08-27) */}
          {renderParams.counterpartyLabel && (
            <div>
              {renderParams.counterpartyLabel}: {renderParams.counterpartyName || (
                <span style={{ display: 'inline-block', width: 260, borderBottom: '1px solid #1a1a1a', minHeight: 22 }} />
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>署名:</span>
            <span
              style={{
                display: 'inline-block',
                width: 260,
                borderBottom: '1px solid #1a1a1a',
                minHeight: 30,
                lineHeight: '30px',
                paddingLeft: 10,
              }}
            >
              {handSignature ? (
                <img src={handSignature} alt="署名" style={{ height: 40, verticalAlign: 'bottom' }} />
              ) : signMode ? (
                // 手書きの代わりにログイン者のフルネームを印字(画面ではマーカーで強調)
                <mark style={{ background: '#FFF176', padding: '2px 10px', fontSize: 18 }}>
                  {user?.display_name}
                </mark>
              ) : (
                ' '
              )}
            </span>
            {/* 押印スポット: クリックで苗字の朱印。署名モード以外は「印」の空欄 */}
            {signMode ? (
              stamped ? (
                <span
                  className="seal"
                  style={{
                    width: 62,
                    height: 62,
                    borderRadius: '50%',
                    border: '3px solid #C0392B',
                    color: '#C0392B',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: renderParams.surname.length <= 2 ? 22 : 15,
                    transform: 'rotate(-4deg)',
                  }}
                >
                  {renderParams.surname}
                </span>
              ) : (
                <button
                  className="no-print"
                  onClick={() => setStamped(true)}
                  style={{
                    width: 62,
                    height: 62,
                    borderRadius: '50%',
                    border: '2px dashed #C0392B',
                    background: '#FFF5F3',
                    color: '#C0392B',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  押印
                </button>
              )
            ) : (
              <span style={{ fontSize: 11, color: '#555' }}>印</span>
            )}
          </div>
          {/* 記入日欄は廃止(2026-08-27 指示) */}
        </div>

        <div style={{ marginTop: 50, paddingTop: 10, borderTop: '1px solid #bbb', fontSize: 11, color: '#555', fontFamily: 'sans-serif' }}>
          Nexus Gate 発行 / 申請者: {renderParams.applicantName} / 支払い方法: {methodLabel} / 支払(予定)日: {tx.payment_date}
          {signMode && stamped ? ` / 電子署名(押印): ${user?.display_name}` : ''}
          {handSignature ? ' / 手書きサイン受領済み' : ''}
        </div>
      </div>

      {padOpen && (
        <SignaturePadModal
          title={`受領のサイン(${tx.counterparty || '支払先'} 様)`}
          onClose={() => setPadOpen(false)}
          onDone={(dataUrl) => {
            setHandSignature(dataUrl);
            setPadOpen(false);
            toast('サインを受け付けました。「PDFで保存」で署名入りの受領書が保存できます');
          }}
        />
      )}
    </div>
  );
}
